import { AdminOnly } from "@/components/AdminOnly";
import { useEffect, useState } from "react";
import { exportXlsx } from "@/lib/xlsx";
import { api, fmt, MONTHS, fmtDMY } from "@/lib/api";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { usePeriod, PeriodPicker, monthAbbr } from "@/context/PeriodContext";
import { PageHeader } from "@/components/PageHeader";
import { LastEntry } from "@/components/LastEntry";
import { MetricCard } from "@/components/MetricCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Stack, Fire, Cylinder, Factory, TrendUp, Cube, Gauge, Recycle, ArrowsClockwise, DownloadSimple, ChartLineUp,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, Cell, ReferenceLine,
} from "recharts";

const COLORS = { C1: "hsl(24 93% 53%)", C2: "hsl(199 89% 48%)", C3: "hsl(142 71% 45%)" };
const SIZE_COLORS = ["hsl(24 93% 53%)", "hsl(199 89% 48%)", "hsl(142 71% 45%)", "hsl(47 96% 53%)"];

const ChartCard = ({ title, children, testid }) => (
  <div className="border border-border bg-card rounded-md p-5" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground mb-4">{title}</div>
    {children}
  </div>
);

const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 2,
    fontFamily: "IBM Plex Mono, monospace",
    fontSize: 12,
  },
};

const MONTH_FULL = { Apr: "April", May: "May", Jun: "June", Jul: "July", Aug: "August", Sep: "September", Oct: "October", Nov: "November", Dec: "December", Jan: "January", Feb: "February", Mar: "March" };

function toCSV(rows, headers, name) {
  exportXlsx(headers, rows, name);
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [theo, setTheo] = useState(null);
  const [err, setErr] = useState(false);
  const [month, setMonth] = useState(null);
  const [issMonth, setIssMonth] = useState("all");
  const [issRows, setIssRows] = useState([]);
  const [dayMonth, setDayMonth] = useState(null);
  const [daily, setDaily] = useState(null);
  const { fy } = useFiscalYear();
  const { month: pMonth } = usePeriod();
  const pAbbr = monthAbbr(pMonth);

  useEffect(() => { setData(null); setMonth(pAbbr); setDayMonth(null); }, [fy, pAbbr]);

  useEffect(() => {
    const mo = dayMonth || month;
    api.get("/dashboard/daily-production", { params: mo ? { month: mo } : {} })
      .then(({ data }) => setDaily(data))
      .catch(() => setDaily(null));
  }, [fy, dayMonth, month]);

  useEffect(() => {
    setErr(false);
    api.get("/production/theoretical", { params: month ? { month } : {} }).then(({ data: t }) => setTheo(t)).catch(() => {});
    api.get("/dashboard/summary", { params: month ? { month } : {} })
      .then(({ data }) => {
        setData(data);
        if (month === null && data.months_with_data?.length && !data.months_with_data.includes(data.current_month)) {
          setMonth(data.months_with_data[data.months_with_data.length - 1]);
        }
      })
      .catch(() => setErr(true));
  }, [fy, month]);

  useEffect(() => {
    api.get("/dashboard/issued-by-machine", { params: { month: issMonth } })
      .then(({ data }) => setIssRows(data.rows || []))
      .catch(() => setIssRows([]));
  }, [fy, issMonth]);

  if (err) return <div className="font-mono text-sm text-destructive">Failed to load dashboard.</div>;
  if (!data) {
    return (
      <div>
        <PageHeader title="Upcast Dashboard" subtitle="Loading plant metrics…" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 border border-border bg-card rounded-md animate-pulse" />)}
        </div>
      </div>
    );
  }

  const sizeData = Object.entries(data.prod_by_size)
    .map(([k, v]) => ({ size: `ø${k}`, num: parseFloat(k), weight: v }))
    .sort((a, b) => a.num - b.num);
  const empty = data.counts.production === 0;
  const monthName = MONTH_FULL[data.current_month] || data.current_month;
  const mpm = data.month_prod_by_machine || { C1: 0, C2: 0, C3: 0 };

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        title="Upcast Dashboard"
        subtitle={`Monthly view · ${monthName} · FY ${data.fiscal_year} · daily figures for ${data.daily_date}`}
      >
        <PeriodPicker />
        <LastEntry date={data.last_entry} />
      </PageHeader>

      <div className="flex items-center gap-3 mb-6" data-testid="dashboard-month-bar">
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Month</span>
        <Select value={data.current_month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-[180px] rounded-md font-mono text-sm" data-testid="dashboard-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((mo) => {
              const has = (data.months_with_data || []).includes(mo);
              return (
                <SelectItem key={mo} value={mo} className="font-mono text-sm" data-testid={`dashboard-month-${mo}`}>
                  {MONTH_FULL[mo] || mo}{has ? "" : " ·"}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <span className="font-mono text-[11px] text-muted-foreground">
          Monthly cards reflect the selected month · daily card always shows the previous day
        </span>
      </div>

      {empty && (
        <div className="border border-primary/40 bg-primary/5 rounded-md p-4 mb-6 font-mono text-sm text-foreground/80" data-testid="empty-hint">
          No data yet. Go to <span className="text-primary font-semibold">Administration</span> to import the FY 2017–18 dataset, or add records manually.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard testid="metric-cathode-balance" to="/cathode-stock" label="Cathode Stock Balance" value={data.cathode_balance} unit="kg" accent hero tone={data.cathode_balance > 0 ? "good" : "bad"} spark={data.monthly_trend} sparkKey="cathode_received" icon={Stack} sub={`As of today · opening ${fmt(data.settings.cathode_opening_stock)} kg`} />
        <MetricCard testid="metric-cathode-received" to="/incoming-cathode" label={`Total Cathode Received · ${data.current_month}`} value={data.month_cathode_received} unit="kg" icon={Cube} sub={`${monthName} intake`} />
        <MetricCard testid="metric-cathode-consumed" to="/cathode-issue" label={`Cathode Consumed · ${data.current_month}`} value={data.month_cathode_consumed} unit="kg" icon={Fire} sub={`${monthName}`} />
        <MetricCard testid="metric-total-production" to="/production" label={`Rod Production · ${data.current_month}`} value={data.month_production} unit="kg" accent hero tone="brand" spark={data.monthly_trend} sparkKey="total" icon={Factory} sub={`${monthName}`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {["C1", "C2", "C3"].map((m) => (
          <MetricCard key={m} testid={`metric-machine-${m}`} to="/production" color={COLORS[m]} label={`Machine ${m} Output · ${data.current_month}`} value={mpm[m]} unit="kg" icon={Factory} sub={`${monthName}`} />
        ))}
        <MetricCard testid="metric-daily-production" to="/production" label="Rod Production · Daily" value={data.daily_production.weight} unit="kg" accent icon={TrendUp} sub={`${data.daily_date} · ${fmt(data.daily_production.coils)} coils`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {["C1", "C2", "C3"].map((m) => (
          <MetricCard key={m} testid={`metric-prod-per-day-${m}`} to="/production" color={COLORS[m]} label={`Avg ${m} · Per Day`} value={data.prod_per_day.machines[m].per_day} unit="kg per day" icon={ChartLineUp} sub={`${fmt(data.prod_per_day.machines[m].total)} kg over ${fmt(data.prod_per_day.machines[m].days)} day${data.prod_per_day.machines[m].days === 1 ? "" : "s"}`} />
        ))}
        <MetricCard testid="metric-prod-per-day" to="/production" label={`Avg Rod Production · Per Day · FY ${data.fiscal_year}`} value={data.prod_per_day.per_day} unit="kg per day" accent icon={ChartLineUp} sub={`${fmt(data.prod_per_day.total)} kg over ${fmt(data.prod_per_day.days)} production day${data.prod_per_day.days === 1 ? "" : "s"}`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <MetricCard testid="metric-scrap-balance" to="/scrap-stock" label="Scrap Stock Balance" value={data.scrap_balance} unit="kg" accent icon={Recycle} sub={data.scrap_by_grade ? `Cu+Ag ${fmt(data.scrap_by_grade["Cu+Ag"].balance)} kg · OFC ${fmt(data.scrap_by_grade.OFC.balance)} kg` : `As of today · opening ${fmt(data.settings.scrap_opening_stock)} kg`} />
        <MetricCard testid="metric-scrap-issued" to="/scrap-issue" label={`Scrap Issued to Prod · ${data.current_month}`} value={data.month_scrap_issued} unit="kg" icon={ArrowsClockwise} sub={`${monthName}`} />
        <MetricCard testid="metric-external-scrap" to="/external-scrap" label={`External Scrap · ${data.current_month}`} value={data.month_external_scrap} unit="kg" icon={Cube} sub={`${monthName} purchased`} />
      </div>

      {theo?.operators?.length > 0 && (
        <div className="mt-4">
          <ChartCard title={`Operator Efficiency Ranking · ${month ? data.current_month : `FY ${data.fiscal_year}`}`} testid="operator-ranking">
            <table className="w-full text-sm dense" data-testid="operator-ranking-table">
              <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="py-1.5 pr-3">#</th><th className="py-1.5 pr-3">Operator</th><th className="py-1.5 pr-3 text-right">Coils</th><th className="py-1.5 pr-3 text-right">Actual (kg)</th><th className="py-1.5 pr-3 text-right">Theoretical (kg)</th><th className="py-1.5 pr-3 text-right">Downtime (min)</th><th className="py-1.5 pr-3 text-right">Efficiency</th><th className="py-1.5 w-1/3"></th>
              </tr></thead>
              <tbody>
                {theo.operators.map((o, i) => { const p = o.efficiency_pct; const thr = theo.config?.low_efficiency_pct ?? 75; const cls = p == null ? "" : p < thr ? "text-red-600" : p >= 90 ? "text-green-700" : "text-amber-700"; return (
                  <tr key={o.operator} className="border-t border-border" data-testid={`operator-rank-${i + 1}`}>
                    <td className="py-2 pr-3 font-mono text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-semibold">{o.operator}</td>
                    <td className="py-2 pr-3 text-right font-mono">{o.coils}</td>
                    <td className="py-2 pr-3 text-right font-mono">{fmt(o.actual)}</td>
                    <td className="py-2 pr-3 text-right font-mono">{fmt(o.theoretical)}</td>
                    <td className="py-2 pr-3 text-right font-mono">{o.downtime_min ? fmt(o.downtime_min) : "—"}</td>
                    <td className={`py-2 pr-3 text-right font-mono font-bold ${cls}`}>{p == null ? "—" : `${p}%`}</td>
                    <td className="py-2"><div className="h-2 bg-muted rounded"><div className={`h-2 rounded ${p < thr ? "bg-[hsl(var(--tone-bad))]" : p >= 90 ? "bg-[hsl(var(--tone-good))]" : "bg-[hsl(var(--tone-warn))]"}`} style={{ width: `${Math.min(100, Math.max(3, p || 0))}%` }} /></div></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </ChartCard>
        </div>
      )}

      <div className="mt-4">
        <ChartCard title={`Cathode & Scrap Issued to Each Machine · ${issMonth === "all" ? `FY ${data.fiscal_year}` : issMonth} (kg)`} testid="table-issued-by-machine">
          <div className="flex justify-end items-center gap-2 mb-3">
            <Select value={issMonth} onValueChange={setIssMonth}>
              <SelectTrigger className="h-8 w-[160px] rounded-md font-mono text-xs" data-testid="issued-month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="font-mono text-xs">All Months (FY)</SelectItem>
                {MONTHS.map((mo) => (
                  <SelectItem key={mo} value={mo} className="font-mono text-xs" data-testid={`issued-month-${mo}`}>{mo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <AdminOnly><Button
              variant="outline"
              size="sm"
              className="h-8 rounded-md font-mono text-xs"
              data-testid="issued-export-csv"
              onClick={() => toCSV(
                [
                  ...issRows.map((r) => [r.machine, r.cathode, r.scrap, r.total]),
                  ["Total", issRows.reduce((s, r) => s + r.cathode, 0), issRows.reduce((s, r) => s + r.scrap, 0), issRows.reduce((s, r) => s + r.total, 0)],
                ],
                ["Machine", "Cathode Issued (kg)", "Scrap Issued (kg)", "Total Issued (kg)"],
                `issued-by-machine-${data.fiscal_year}-${issMonth}.csv`,
              )}
            >
              <DownloadSimple size={15} weight="bold" className="mr-1.5" /> Export to xls
            </Button></AdminOnly>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="issued-by-machine-table">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground border-b border-border">
                    <th className="text-left py-2 px-3">Machine</th>
                    <th className="text-right py-2 px-3">Cathode Issued</th>
                    <th className="text-right py-2 px-3">Scrap Issued</th>
                    <th className="text-right py-2 px-3">Total Issued</th>
                  </tr>
                </thead>
                <tbody>
                  {issRows.map((r) => (
                    <tr key={r.machine} className="border-b border-border/50" data-testid={`issued-row-${r.machine}`}>
                      <td className="py-2 px-3 font-semibold" style={{ color: COLORS[r.machine] }}>{r.machine}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmt(r.cathode)}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmt(r.scrap)}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                  <tr className="font-mono" data-testid="issued-row-total">
                    <td className="py-2 px-3 font-bold uppercase text-[11px] tracking-wider">Total</td>
                    <td className="py-2 px-3 text-right font-bold">{fmt(issRows.reduce((s, r) => s + r.cathode, 0))}</td>
                    <td className="py-2 px-3 text-right font-bold">{fmt(issRows.reduce((s, r) => s + r.scrap, 0))}</td>
                    <td className="py-2 px-3 text-right font-bold text-primary">{fmt(issRows.reduce((s, r) => s + r.total, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={issRows} barCategoryGap="25%" barGap={6}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="machine" tick={{ fontFamily: "IBM Plex Mono", fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <Bar dataKey="cathode" name="Cathode Issued" fill="hsl(24 93% 53%)" />
                <Bar dataKey="scrap" name="Scrap Issued" fill="hsl(199 89% 48%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mt-4">
        <ChartCard title={`Daily Production · Machine-wise · ${MONTH_FULL[daily?.month] || daily?.month || ""} FY ${data.fiscal_year} (kg)`} testid="card-daily-production">
          <div className="flex justify-end items-center gap-2 mb-3">
            <Select value={dayMonth || daily?.month || month || data.current_month} onValueChange={setDayMonth}>
              <SelectTrigger className="h-8 w-[160px] rounded-md font-mono text-xs" data-testid="daily-month-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((mo) => <SelectItem key={mo} value={mo} className="font-mono text-xs" data-testid={`daily-month-${mo}`}>{MONTH_FULL[mo] || mo}</SelectItem>)}
              </SelectContent>
            </Select>
            <AdminOnly><Button variant="outline" size="sm" className="h-8 rounded-md font-mono text-xs" data-testid="daily-export" disabled={!daily?.rows?.length}
              onClick={() => toCSV(
                [...(daily?.rows || []).map((r) => [r.date, r.C1, r.C2, r.C3, r.total, r.total_coils]), ["Total", daily.totals.C1, daily.totals.C2, daily.totals.C3, daily.totals.total, daily.totals.total_coils]],
                ["Date", "C1 (kg)", "C2 (kg)", "C3 (kg)", "Total (kg)", "Coils"], `daily-production-${data.fiscal_year}-${daily?.month}.xlsx`)}>
              <DownloadSimple size={15} weight="bold" className="mr-1.5" /> Export to xls
            </Button></AdminOnly>
          </div>
          {!daily || daily.rows.length === 0 ? <div className="font-mono text-sm text-muted-foreground py-6 text-center" data-testid="daily-empty">No production recorded in {MONTH_FULL[daily?.month] || "this month"}</div> : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="daily-production-table">
                  <thead className="sticky top-0 bg-card">
                    <tr className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-3">Date</th>
                      {["C1", "C2", "C3"].map((m) => <th key={m} className="text-right py-2 px-3" style={{ color: COLORS[m] }}>{m}</th>)}
                      <th className="text-right py-2 px-3">Total</th>
                      <th className="text-right py-2 px-3">Coils</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.rows.map((r) => (
                      <tr key={r.date} className="border-b border-border/50" data-testid={`daily-row-${r.date}`}>
                        <td className="py-1.5 px-3 font-mono">{fmtDMY(r.date)}</td>
                        {["C1", "C2", "C3"].map((m) => <td key={m} className={`py-1.5 px-3 text-right font-mono ${r[m] ? "" : "text-muted-foreground/50"}`}>{r[m] ? fmt(r[m]) : "—"}</td>)}
                        <td className="py-1.5 px-3 text-right font-mono font-semibold">{fmt(r.total)}</td>
                        <td className="py-1.5 px-3 text-right font-mono text-muted-foreground">{r.total_coils}</td>
                      </tr>
                    ))}
                    <tr className="font-mono bg-muted/40" data-testid="daily-row-total">
                      <td className="py-2 px-3 font-bold uppercase text-[11px] tracking-wider">Total · {daily.days} days</td>
                      {["C1", "C2", "C3"].map((m) => <td key={m} className="py-2 px-3 text-right font-bold">{fmt(daily.totals[m])}</td>)}
                      <td className="py-2 px-3 text-right font-bold text-primary">{fmt(daily.totals.total)}</td>
                      <td className="py-2 px-3 text-right font-bold">{daily.totals.total_coils}</td>
                    </tr>
                    <tr className="font-mono" data-testid="daily-row-avg">
                      <td className="py-2 px-3 uppercase text-[11px] tracking-wider text-muted-foreground">Avg / day</td>
                      {["C1", "C2", "C3"].map((m) => <td key={m} className="py-2 px-3 text-right text-muted-foreground">{fmt(daily.avg_per_day[m])}</td>)}
                      <td className="py-2 px-3 text-right font-semibold">{fmt(daily.avg_per_day.total)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={daily.rows.map((r) => ({ ...r, day: r.date.slice(8) }))} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                  {["C1", "C2", "C3"].map((m) => <Bar key={m} dataKey={m} stackId="d" name={m} fill={COLORS[m]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2">
          <ChartCard title="Monthly Production by Machine (kg)" testid="chart-monthly">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthly_trend} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <Bar dataKey="C1" stackId="a" fill={COLORS.C1} />
                <Bar dataKey="C2" stackId="a" fill={COLORS.C2} />
                <Bar dataKey="C3" stackId="a" fill={COLORS.C3} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Production by Rod Size (kg)" testid="chart-size">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sizeData} layout="vertical">
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="size" tick={{ fontFamily: "IBM Plex Mono", fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={50} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="weight" radius={0}>
                {sizeData.map((_, i) => <Cell key={i} fill={SIZE_COLORS[i % SIZE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-4">
        <ChartCard title="Total Monthly Production Trend (kg)" testid="chart-trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.monthly_trend}>
              <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip {...tooltipStyle} />
              <Line type="linear" dataKey="total" stroke="hsl(24 93% 53%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(24 93% 53%)" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {theo?.monthly && (
        <div className="mt-4">
          <ChartCard title={`Monthly Efficiency % · Actual vs Theoretical · FY ${data.fiscal_year}`} testid="chart-efficiency">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={theo.monthly}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[0, 120]} unit="%" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip {...tooltipStyle} formatter={(v) => (v == null ? "—" : `${v}%`)} />
                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <ReferenceLine y={Number(theo.config?.low_efficiency_pct ?? 75)} stroke="hsl(0 72% 51%)" strokeDasharray="4 4" label={{ value: `Alert ${theo.config?.low_efficiency_pct ?? 75}%`, position: "right", fontSize: 10, fill: "hsl(0 72% 51%)" }} />
                {["C1", "C2", "C3"].map((m) => <Line key={m} type="monotone" dataKey={m} name={m} stroke={COLORS[m]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
                <Line type="monotone" dataKey="total" name="Plant" stroke="hsl(var(--foreground))" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
